import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {DateTime} from "luxon";
import {GAME_TIMEZONE} from "data/constants";
import {ChallengeViewType, ChallengeRosterSource} from "data/enums/challenge";
import type {IUserRosterStore} from "data/stores/user_roster/user_roster.store";
import {RequestState, ModalType} from "data/enums";
import {extractErrorMessage} from "data/utils";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {Empty} from "data/types/global";
import {IRosterItem} from "data/types/roster";
import type {IGameBar, IGameBarStore} from "data/stores/game_bar/game_bar.store";
import {useNavigate} from "react-router-dom";

interface IParams {
	userId: number;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IH2hChallengeViewController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get contestId(): number;

	get contestDay(): string;

	get challengeView(): ChallengeViewType;

	get userSource(): ChallengeRosterSource;

	get opponentRoster(): Empty<IRosterItem>;

	get opponentGameBar(): Empty<IGameBar>;

	get userGameBar(): Empty<IGameBar>;

	get opponentDisplayName(): string;

	get isOwnViewActive(): boolean;

	get isOpponentViewActive(): boolean;

	toggleChallengeView: () => void;
	toggleSourceView: () => void;
}

@injectable()
export class H2hChallengeViewController implements IH2hChallengeViewController {
	@observable _userId: number = 0;
	@observable _view: ChallengeViewType = ChallengeViewType.H2H;
	@observable _userSource: ChallengeRosterSource = ChallengeRosterSource.YOU;
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _navigate?: IParams["navigate"];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) public readonly _contestsStore: IContestsStore,
		@inject(Bindings.UserRosterStore) public readonly _userRosterStore: IUserRosterStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore,
		@inject(Bindings.GameBarStore) public readonly _gameBarStore: IGameBarStore
	) {
		makeAutoObservable(this);
	}

	get contestId(): number {
		return this._contestsStore.currentContest?.id || 0;
	}

	get challengeView(): ChallengeViewType {
		return this._view;
	}

	get userSource(): ChallengeRosterSource {
		return this._userSource;
	}

	get opponentRoster() {
		return this._userRosterStore.userRoster;
	}

	get opponentGameBar() {
		return this._userRosterStore.userGameBar;
	}

	get userGameBar() {
		return this._gameBarStore.gameBar;
	}

	get opponentDisplayName() {
		return this._userRosterStore.displayName || "--";
	}

	get isOwnViewActive(): boolean {
		return this._userSource === ChallengeRosterSource.YOU;
	}

	get isOpponentViewActive(): boolean {
		return this._userSource === ChallengeRosterSource.OPPONENT;
	}

	get contestDay(): string {
		const currentContest = this._contestsStore.currentContest;
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("L/dd") : "-";
	}

	toggleChallengeView = () => {
		this._view =
			this._view === ChallengeViewType.FULL ? ChallengeViewType.H2H : ChallengeViewType.FULL;
	};

	toggleSourceView = () => {
		this._userSource =
			this._userSource === ChallengeRosterSource.YOU
				? ChallengeRosterSource.OPPONENT
				: ChallengeRosterSource.YOU;
	};

	init(param: IParams) {
		this._navigate = param.navigate;
		this._userId = param.userId;
		this.showRoster();
	}

	onChange(param: IParams) {
		this.init(param);
	}

	showRoster = () => {
		this._requestState = RequestState.PENDING;
		if (this._userId) {
			void this._userRosterStore
				.fetchUserRoster(this.contestId, this._userId)
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});

					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});

					this.toGames();
				});
		}
	};

	private toGames = () => {
		this._navigate?.("/head_to_head/games");
	};
}
